/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sprout, ShieldCheck, Wifi, LogIn, UserPlus } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function Hero() {
  const { t, lang } = useLang();
  const { user } = useAuth();

  const dashboardPath =
    user?.role === "owner"
      ? "/dashboard/owner"
      : user?.role === "trainer"
      ? "/dashboard/trainer"
      : "/dashboard/client";

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full opacity-[0.06] bg-[radial-gradient(#1C3A27_1px,transparent_1px)] [background-size:14px_14px]"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 rise">
          <div
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]"
          >
            <Sprout size={16} />
            {t.hero.overline}
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] text-[#1C3A27]"
          >
            {t.hero.headline_a}{" "}
            <span className="marker-accent italic font-black">
              {t.hero.headline_b}
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 text-lg md:text-xl text-[#5C6B5C] leading-relaxed max-w-xl"
          >
            {t.hero.subheadline}
          </p>

          {/* Primary CTAs: big & visible — replaces the old waitlist form */}
          {user ? (
            <div data-testid="hero-cta-group" className="mt-10 max-w-xl">
              <Button
                asChild
                data-testid="hero-cta-dashboard"
                className="w-full sm:w-auto h-14 px-7 rounded-2xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold text-base group"
              >
                <Link to={dashboardPath}>
                  <LogIn size={18} className="me-2" />
                  {t.hero.cta_dashboard}
                  <ArrowRight
                    size={18}
                    className={`ms-2 group-hover:translate-x-1 transition-transform ${
                      lang === "ar" ? "rotate-180" : ""
                    }`}
                  />
                </Link>
              </Button>
            </div>
          ) : (
            <div
              data-testid="hero-cta-group"
              className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl"
            >
              <Button
                asChild
                data-testid="hero-cta-signup"
                className="h-14 px-6 rounded-2xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold text-base group shadow-lg shadow-[#E05A3D]/20"
              >
                <Link to="/signup">
                  <UserPlus size={18} className="me-2" />
                  {t.hero.cta_primary}
                  <ArrowRight
                    size={18}
                    className={`ms-2 group-hover:translate-x-1 transition-transform ${
                      lang === "ar" ? "rotate-180" : ""
                    }`}
                  />
                </Link>
              </Button>
              <Button
                asChild
                data-testid="hero-cta-login"
                variant="outline"
                className="h-14 px-6 rounded-2xl border-2 border-[#1C3A27] bg-white hover:bg-[#1C3A27] hover:text-white text-[#1C3A27] font-semibold text-base"
              >
                <Link to="/login">
                  <LogIn size={18} className="me-2" />
                  {t.hero.cta_login}
                </Link>
              </Button>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-[#5C6B5C]">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#1C3A27]" />
              {t.hero.trust_hosting}
            </div>
            <div className="flex items-center gap-2">
              <Wifi size={16} className="text-[#1C3A27]" />
              {t.hero.trust_offline}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E05A3D]" />
              {t.hero.trust_trial}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 rise delay-2">
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-3 rounded-[2rem] bg-[#1C3A27]/10 rotate-2"
            />
            <div className="relative rounded-[2rem] overflow-hidden border border-[#E2E4DF] bg-white shadow-xl">
              <div className="grid grid-cols-2 grid-rows-2 gap-1.5 p-1.5 h-[460px] md:h-[520px] bg-[#1C3A27]">
                <img
                  src="/landing/hero_main.jpg"
                  alt="Éleveur tunisien avec son troupeau d'Ouled Djellal"
                  className="row-span-2 w-full h-full object-cover rounded-xl"
                />
                <img
                  src="/landing/hero_sheep.jpg"
                  alt="Mouton Ouled Djellal en pâturage"
                  className="w-full h-full object-cover rounded-xl"
                />
                <img
                  src="/landing/hero_olives.jpg"
                  alt="Oliveraie tunisienne du sud (Médenine)"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              <div
                data-testid="hero-stat-card"
                className="absolute bottom-5 left-5 max-w-[320px] bg-white/95 backdrop-blur-md rounded-2xl border border-[#E2E4DF] p-4 flex items-center gap-4 shadow-lg"
              >
                <div className="w-11 h-11 rounded-xl bg-[#1C3A27] grid place-items-center text-white font-display font-bold shrink-0">
                  214
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C6B5C] font-semibold truncate">
                    {t.hero.stat_card_label}
                  </div>
                  <div className="text-xs text-[#1C3A27] font-medium mt-0.5 truncate">
                    {t.hero.stat_card_sub}
                  </div>
                </div>
                <div className="text-[#E05A3D] font-display font-bold text-xl">
                  ↗
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
        <div className="dashed-divider" />
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {t.hero.stats.map(([k, v], i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div
                dir="ltr"
                className="font-display text-3xl md:text-4xl font-black text-[#1C3A27]"
              >
                {k}
              </div>
              <div className="text-sm text-[#5C6B5C] mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
